import * as React from 'react';

const ALLOWED_PROTOCOLS = new Set(['http:', 'https:', 'mailto:']);

const BLOCKED_SCHEME_PREFIXES = [
  'javascript:',
  'data:',
  'vbscript:',
  'file:',
  'blob:',
] as const;

/** Underline plus the same keyboard focus ring used by HEN links. */
export const MINIMAL_MARKDOWN_LINK_CLASS_NAME = [
  'underline',
  'underline-offset-4',
  'focus-visible:outline-none',
  'focus-visible:ring-2',
  'focus-visible:ring-focus',
  'focus-visible:ring-offset-1',
  'focus-visible:ring-offset-focus',
].join(' ');

export type ParseMinimalMarkdownOptions = {
  /** Applied to parsed link tokens. Chart footnotes and detail rows use {@link MINIMAL_MARKDOWN_LINK_CLASS_NAME}. */
  linkClassName?: string;
};

export type SafeMarkdownHref = {
  safeHref: string;
  target?: string;
  rel?: string;
};

type MarkdownToken =
  | { type: 'text'; value: string }
  | { type: 'bold'; value: string }
  | { type: 'italic'; value: string }
  | { type: 'link'; text: string; href: string };

const EXPLICIT_SCHEME_PATTERN = /^[a-zA-Z][a-zA-Z\d+.-]*:/;

function hasControlCharacters(value: string): boolean {
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) {
      return true;
    }
  }
  return false;
}

function isBlockedScheme(value: string): boolean {
  const lower = value.toLowerCase();
  return BLOCKED_SCHEME_PREFIXES.some((prefix) => lower.startsWith(prefix));
}

/**
 * Allowlist link hrefs for minimal markdown (`[text](url)`).
 * Matches chart tooltip footer behavior.
 */
export function resolveSafeMarkdownHref(rawHref: string): SafeMarkdownHref {
  if (hasControlCharacters(rawHref)) {
    return { safeHref: '#' };
  }

  const trimmed = rawHref.trim();

  if (!trimmed || isBlockedScheme(trimmed)) {
    return { safeHref: '#' };
  }

  let safeHref = '#';
  let target: string | undefined;
  let rel: string | undefined;

  const hasExplicitScheme = EXPLICIT_SCHEME_PATTERN.test(trimmed);
  const isRelativeHref =
    !trimmed.startsWith('//') &&
    (trimmed.startsWith('/') ||
      trimmed.startsWith('./') ||
      trimmed.startsWith('../') ||
      trimmed.startsWith('?') ||
      trimmed.startsWith('#'));

  if (isRelativeHref) {
    safeHref = trimmed;
  } else if (hasExplicitScheme) {
    try {
      const url = new URL(trimmed);
      const protocol = url.protocol.toLowerCase();

      if (isBlockedScheme(protocol) || !ALLOWED_PROTOCOLS.has(protocol)) {
        return { safeHref: '#' };
      }

      safeHref =
        url.pathname === '/' && !trimmed.endsWith('/')
          ? `${url.origin}${url.search}${url.hash}`
          : url.href;

      // Absolute http(s) links always open in a new tab. Comparing against
      // window.location.origin makes the server omit target/rel and the
      // client add them, which mismatches during hydration.
      if (protocol === 'http:' || protocol === 'https:') {
        target = '_blank';
        rel = 'noopener noreferrer';
      }
    } catch {
      // Malformed absolute URL — keep '#'
    }
  }

  return { safeHref, target, rel };
}

function tryParseLink(
  raw: string,
  start: number,
): { text: string; href: string; length: number } | null {
  if (raw[start] !== '[') {
    return null;
  }

  const labelEnd = raw.indexOf(']', start + 1);
  if (labelEnd === -1 || raw[labelEnd + 1] !== '(') {
    return null;
  }

  let depth = 0;
  for (let i = labelEnd + 2; i < raw.length; i++) {
    const char = raw[i];
    if (char === '(') {
      depth += 1;
    } else if (char === ')') {
      if (depth === 0) {
        return {
          text: raw.slice(start + 1, labelEnd),
          href: raw.slice(labelEnd + 2, i),
          length: i - start + 1,
        };
      }
      depth -= 1;
    }
  }

  return null;
}

function pushTextToken(tokens: MarkdownToken[], value: string): void {
  if (!value) {
    return;
  }
  const last = tokens.at(-1);
  if (last?.type === 'text') {
    last.value += value;
  } else {
    tokens.push({ type: 'text', value });
  }
}

function tokenizeMinimalMarkdown(raw: string): MarkdownToken[] {
  const tokens: MarkdownToken[] = [];
  let i = 0;

  while (i < raw.length) {
    if (raw.startsWith('**', i)) {
      const end = raw.indexOf('**', i + 2);
      if (end !== -1 && end > i + 2) {
        tokens.push({ type: 'bold', value: raw.slice(i + 2, end) });
        i = end + 2;
        continue;
      }
    }

    if (raw[i] === '*' && raw[i + 1] !== '*') {
      const end = raw.indexOf('*', i + 1);
      if (end !== -1 && raw[end + 1] !== '*') {
        tokens.push({ type: 'italic', value: raw.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }

    const link = tryParseLink(raw, i);
    if (link) {
      tokens.push({ type: 'link', text: link.text, href: link.href });
      i += link.length;
      continue;
    }

    pushTextToken(tokens, raw[i] ?? '');
    i += 1;
  }

  return tokens;
}

/**
 * Parse operator markdown limited to `**bold**`, `*italic*`, and `[text](url)`.
 * Not CommonMark — no lists, images, code blocks, or nested markdown in bold.
 * Returns React nodes — no HTML string, no DOMPurify.
 */
export function parseMinimalMarkdown(
  raw: string,
  options: ParseMinimalMarkdownOptions = {},
): React.ReactNode[] {
  if (typeof raw !== 'string' || raw.length === 0) {
    return [];
  }

  const { linkClassName } = options;

  return tokenizeMinimalMarkdown(raw).map((token, index) => {
    if (token.type === 'bold') {
      return <strong key={index}>{token.value}</strong>;
    }

    if (token.type === 'italic') {
      return <em key={index}>{token.value}</em>;
    }

    if (token.type === 'link') {
      const { safeHref, target, rel } = resolveSafeMarkdownHref(token.href);
      return (
        <a
          key={index}
          href={safeHref}
          {...(target ? { target } : {})}
          {...(rel ? { rel } : {})}
          {...(linkClassName ? { className: linkClassName } : {})}
        >
          {token.text}
        </a>
      );
    }

    return token.value || null;
  });
}
